/** 마지막 성공값을 공유하고 세션 종료에 폐기하는 가시 탭 폴링. */
import { isPageHidden } from "@/shared/utils/pageVisibility";

export type PollingSnapshot<Data> = {
  data: Data | null;
  loading: boolean;
  failed: boolean;
};

type PollingOptions<Data> = {
  load: (signal: AbortSignal) => Promise<Data>;
  intervalMs: number;
  onLoadError: (error: unknown) => void;
};

const emptySnapshot = <Data>(): PollingSnapshot<Data> => ({
  data: null,
  loading: true,
  failed: false,
});

// BE 배포 정보와 FE 업데이트 감지가 세션·탭 복귀·취소 정책을 공유한다.
export class PollingController<Data> {
  private snapshot = emptySnapshot<Data>();
  private readonly listeners = new Set<() => void>();
  private active = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private request: AbortController | null = null;

  constructor(private readonly options: PollingOptions<Data>) {}

  getSnapshot = (): PollingSnapshot<Data> => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    if (this.active && this.listeners.size === 1) this.onVisibilityChange();
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        this.stopTimer();
        this.request?.abort();
        this.request = null;
      }
    };
  };

  init(): void {
    if (this.active) return;
    this.active = true;
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.onVisibilityChange();
  }

  dispose(): void {
    this.active = false;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.stopTimer();
    this.request?.abort();
    this.request = null;
    this.publish(emptySnapshot<Data>());
  }

  refresh = async (): Promise<void> => {
    if (!this.active || this.request !== null) return;
    const request = new AbortController();
    this.request = request;
    try {
      const data = await this.options.load(request.signal);
      if (this.request === request) this.publish({ data, loading: false, failed: false });
    } catch (error: unknown) {
      if (this.request !== request) return;
      this.options.onLoadError(error);
      this.publish({ ...this.snapshot, loading: false, failed: true });
    } finally {
      if (this.request === request) this.request = null;
    }
  };

  private readonly onVisibilityChange = (): void => {
    this.stopTimer();
    if (this.listeners.size === 0 || isPageHidden()) return;
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), this.options.intervalMs);
  };

  private stopTimer(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  private publish(snapshot: PollingSnapshot<Data>): void {
    if (
      Object.is(this.snapshot.data, snapshot.data) &&
      this.snapshot.loading === snapshot.loading &&
      this.snapshot.failed === snapshot.failed
    )
      return;
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener());
  }
}
